import { FC, useCallback, useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { useTranslation } from "react-i18next";
import packageJson from '../../package.json'
import { AppContext } from '../contexts/AppContext';
import { getIcon } from '../common/Common';
import { getTranslation } from '../common/Translation';
import { u } from '../common/Units';
import { MenuDisplayMode } from '../common/MenuDisplay';
import { fetchDocument } from '../services/Api';

export { MENU_WIDTH_BASE, getMenuOffset } from '../common/Units';

interface MenuProps {
  /** Resolved display mode; null when there are too few items to show a menu */
  displayMode: MenuDisplayMode | null,
  /** Overlay open state (left sidebar is always open) */
  isOpen: boolean,
  close: () => void
}

const MenuBackdrop = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  height: 100%;
  z-index: 30;
  background-color: rgba(0, 0, 0, .5);

  &.closed {
    opacity: 0;
    display: none;
  }
`;

const MenuWrapper = styled.aside<{ $side: "left" | "right" }>`
  position: fixed;
  top: 0;
  bottom: 0;
  width: var(--menu-width);
  background-color: ${p => p.theme?.backgroundColor};
  background-color: color-mix(in srgb, ${p => p.theme?.backgroundColor} 85%, black);
  z-index: 31;
  transition: transform 0.2s linear;
  ${p => p.$side === "left" ? css`
    left: 0;
    box-shadow: 8px -1px 52px -4px rgba(0, 0, 0, .3);
  ` : css`
    right: 0;
    box-shadow: -8px -1px 52px -4px rgba(0, 0, 0, .3);
  `}

  &.closed {
    transform: translateX(${p => p.$side === "left" ? "-100%" : "100%"});
    box-shadow: none;
  }

  &.persistent {
    z-index: 1;
    box-shadow: none;
    border-right: 1px solid rgba(255, 255, 255, 0.08);
    /* Static chrome: never slide, so switching from the overlay side does not animate across the screen */
    transform: none;
    transition: none;
  }
`;

const MenuItemsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: calc(100% - ${u(110)});
  overflow-y: auto;
`

const MenuItems = styled.ul`
  /* Narrow side margins, so the titles get as much room as possible */
  margin: ${u(45)} 12px 0;
  padding: 0;
  list-style: none;
  background: transparent;

  & li {
    cursor: pointer;
    background: transparent;
  }

  & li + li {
    margin-top: ${u(4)};
  }

  &.preview {
    pointer-events: none;
  }
`

const MenuLink = styled(Link)`
  display: flex;
  flex-wrap: nowrap;
  /* The icon stays at the first line when a title wraps */
  align-items: flex-start;
  padding: ${u(12)} ${u(16)};
  border-radius: ${u(14)};
  box-sizing: border-box;
  color: ${p => p.theme?.textColor};
  font-size: 1.05em;
  text-decoration: none !important;
  transition: background-color 0.15s;

  /* Margins instead of flex gap, which needs iOS 14.5 */
  & > * + * {
    margin-left: ${u(16)};
  }

  & svg:first-of-type {
    fill: ${p => p.theme?.textColor};
    width: ${u(22)};
    height: ${u(22)};
    flex-shrink: 0;
    /* Centered on the first line of the title (line-height 1.25em, see MenuLabel) */
    margin-top: calc((1.25em - ${u(22)}) / 2);
    opacity: 0.7;
  }

  /* Feedback when tapped */
  &:active {
    background-color: rgba(255, 255, 255, 0.06);
    background-color: color-mix(in srgb, ${p => p.theme?.textColor} 8%, transparent);
  }

  li.active & {
    background-color: ${p => p.theme?.accentColor || "#3F7DF0"};
    font-weight: 500;
    box-shadow: 0 ${u(4)} ${u(14)} rgba(0, 0, 0, 0.2);
  }

  li.active & svg:first-of-type {
    opacity: 1;
  }
`

/** Long titles wrap to two lines, then end with an ellipsis */
const MenuLabel = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-height: 1.25;
  overflow-wrap: break-word;
  text-decoration: none !important;
  background: transparent;
`

const VersionFooter = styled.span`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: ${u(26)} ${u(28)};
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 0.75em;
  text-align: left;
  opacity: 0.6;
  background: transparent;
`

const VersionNumber = styled.span`
  margin-left: ${u(4)};
  font-size: 0.85em;
  opacity: 0.7;
`

const BottomBar = styled.nav`
  position: fixed;
  left: var(--menu-offset, 0px);
  right: 0;
  bottom: 0;
  z-index: 20;
  padding: 0;
  box-sizing: border-box;
  /* Only the pills take input, so content behind the empty parts of the bar stays clickable */
  pointer-events: none;
`

const BottomScroller = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  touch-action: pan-x;
  -webkit-overflow-scrolling: touch;
  pointer-events: none;
  padding: ${u(24)} 0 ${u(19)};
`

const BottomItems = styled.ul`
  display: flex;
  flex-wrap: nowrap;
  /* max-content + auto margins: centered when it fits, left-aligned and swipeable when it does not */
  width: max-content;
  margin: 0 auto;
  /* Start at the article's left margin when scrolling; vertical room keeps the pill shadow inside the scroller's clip */
  padding: ${u(16)} 4vw;
  box-sizing: content-box;
  list-style: none;
  /* Only the buttons take input. The padding around them lets content close to the bar stay clickable */
  pointer-events: none;

  & > li {
    pointer-events: auto;
  }

  /* Margins instead of flex gap, which needs iOS 14.5 */
  & > li + li {
    margin-left: ${u(16)};
  }

  &.preview > li {
    pointer-events: none;
  }
`

const BottomLink = styled(Link)`
  display: flex;
  align-items: center;
  padding: ${u(12)} ${u(26)};
  border-radius: 999px;
  background-color: ${p => p.theme?.accentColor}80;
  background-color: color-mix(in srgb, ${p => p.theme?.accentColor} 60%, #1c2936);
  color: ${p => p.theme?.textColor};
  text-decoration: none;
  white-space: nowrap;
  font-weight: 500;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.12), 0 4px 8px rgba(0, 0, 0, 0.18), 0 12px 32px rgba(0, 0, 0, 0.18);

  & svg {
    fill: currentColor;
    width: ${u(20)};
    height: ${u(20)};
    flex-shrink: 0;
    margin-right: ${u(12)};
  }

  &:active {
    filter: brightness(.9);
  }

  li.active & {
    background-color: ${p => p.theme?.accentColor};
    font-weight: 600;
  }
`

/**
 * Render the device menu as a right overlay, a fixed left sidebar or a bottom bar.
 * Always mounted, since it also builds deviceMenu for the rest of the app.
 * @returns {JSX.Element} Component template
 */
// Header (and thus Menu) remounts on every navigation, so keep bottom bar scroll outside React
let bottomScrollLeft = 0;

const Menu: FC<MenuProps> = ({displayMode, isOpen, close}) => {
  const bottomScrollerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (bottomScrollerRef.current) {
      bottomScrollerRef.current.scrollLeft = bottomScrollLeft;
    }
  });

  const { currentLanguage, setCurrentLanguage, setDeviceLanguages, deviceId, deviceData, deviceMenu, setDeviceMenu, deviceLanguages, previewIsActive, scheduledIds } = useContext(AppContext);
  const { t } = useTranslation("common");
  const persistent = displayMode === "left";
  const side: "left" | "right" = persistent ? "left" : "right";

  // Lets other elements react to the open overlay menu, e.g. ScrollIndicator hides itself
  useEffect(() => {
    const overlayIsOpen = isOpen && !persistent && displayMode !== "bottom";
    document.body.classList.toggle("menu-open", overlayIsOpen);
    return () => { document.body.classList.remove("menu-open"); };
  }, [isOpen, persistent, displayMode]);

  // Set available languages
  useEffect(() => {
    // Collect device languages and set default language
    if (deviceData?.languages && deviceData?.languages?.filter(lang => lang != null)?.length !== deviceLanguages?.length) {
      setDeviceLanguages(deviceData?.languages);
      //setCurrentLanguage(deviceData?.languages?.[0]);
    }
  }, [setDeviceLanguages, deviceLanguages, deviceData]);

  // Build menu
  useEffect(() => {
    if (!deviceData || !(Symbol.iterator in Object(deviceData?.presentations?.list)) || deviceData?.presentations?.list?.length < 2) { 
      setDeviceMenu([]);
      return 
    }

    let menuData: any = [];

    (async() => {
      await Promise.all(
        (scheduledIds ?? deviceData?.presentations?.list ?? []).map(async (item: any, i: number) => {
          const presentationId = scheduledIds ? String(item) : String(item?.to_document_id);
          const presentationData = await fetchDocument(presentationId);
      
          const presentationTitle = presentationData?.content?.general?.title || presentationData?.title;
          const presentationTitleOverride = scheduledIds ? undefined : item?.content?.title;
          const presentationType = presentationData?.content?.general?.presentationType || "article";
      
          // Collect menu hierarchy
          if (presentationData?.status === "published") {
            menuData[i] = {
              id: presentationId,
              title: presentationTitle,
              titleOverride: presentationTitleOverride,
              type: presentationType,
              url: `/device/${deviceId}/${presentationType}/${presentationId}`,
              languages: Object.keys(presentationData?.content?.general?.title || []) || []
            };
          }
      
          return menuData;
        })
      ).then(a => {
        // Add home page to the menu if needed
        if (deviceData?.general?.startScreen === "menu" && !scheduledIds) {
          menuData.unshift({
            title: "t(menu.home)",
            type: "home",
            url: `/device/${deviceId}`
          });
        }
        setDeviceMenu(menuData)
      });
    })();
  }, [deviceId, deviceData, setCurrentLanguage, setDeviceMenu, scheduledIds])

  const closeAfterClick = useCallback(() => {
    if (persistent) { return; }
    setTimeout(() => {
      close();
    }, 200);
  }, [close, persistent]);

  /**
   * Returns true if current page is home
   */
   const isHome = /device\/(\d+)(\/)?$/.test(window?.location?.hash)

  // Entries visible in the current language, with resolved titles
  const visibleLinks = (deviceMenu || [])
    .map((link: any, i: number) => {
      if (!link || !(link?.languages?.includes(currentLanguage) || link.type === "home")) { return null; }
      // Use i18n if title is formatted as "t(Lorem ipsum)"
      const i18match = (String(link?.title).match(/t\((.*)\)/));
      const titleOverride = getTranslation(link?.titleOverride, currentLanguage)?.trim()?.length > 0 ? getTranslation(link?.titleOverride, currentLanguage) : undefined;
      return {
        key: `linkitem-${i}`,
        url: link?.url,
        type: link?.type,
        title: i18match ? t(i18match) : titleOverride || getTranslation(link?.title, currentLanguage),
        isActive: window?.location?.href.endsWith(link?.url) || ((isHome || previewIsActive) && i === 0)
      };
    })
    .filter(Boolean);

  if (displayMode === "bottom") {
    return (
      <BottomBar id="menubar">
        <BottomScroller ref={bottomScrollerRef} onScroll={e => { bottomScrollLeft = e.currentTarget.scrollLeft; }}>
          <BottomItems className={previewIsActive ? "preview" : undefined}>
            {visibleLinks.map((link: any) => (
              <li key={link.key} className={link.isActive ? "active" : undefined}>
                <BottomLink to={link.url} aria-current={link.isActive ? "page" : undefined}>
                  {getIcon(link.type)}
                  {link.title}
                </BottomLink>
              </li>
            ))}
          </BottomItems>
        </BottomScroller>
      </BottomBar>
    );
  }

  return (
    <>
    {isOpen && !persistent && (<MenuBackdrop className={isOpen ? "open" : "closed"} onClick={close}></MenuBackdrop>)}
    <MenuWrapper $side={side} className={`${isOpen ? "open" : "closed"}${persistent ? " persistent" : ""}`}>
      <MenuItemsWrapper>
        <MenuItems className={previewIsActive ? "preview" : undefined}>
          {visibleLinks.map((link: any) => (
            <li key={link.key} className={link.isActive ? "active" : undefined}>
              <MenuLink to={link.url} onClick={closeAfterClick}>
                {getIcon(link.type)}
                <MenuLabel>{link.title}</MenuLabel>
              </MenuLink>
            </li>
          ))}
        </MenuItems>
      </MenuItemsWrapper>
      {side === "right" && (
        <VersionFooter>
          {t("menu.poweredBy")} {packageJson.displayName} <VersionNumber>v{packageJson.version}</VersionNumber>
        </VersionFooter>
      )}
      </MenuWrapper>
    </>
  )
}
export default Menu;
